import { Alert, Button, Card, Descriptions, Empty, Space, Typography } from 'antd';
import { CopyOutlined, EnvironmentOutlined, GlobalOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { DeviceDetailDto } from '@/types/device';

const { Text, Paragraph } = Typography;

interface Props {
  device: DeviceDetailDto;
}

/**
 * Mapa de ubicacion del device usando Google Maps Embed API.
 *
 * Necesita una API key con permiso para "Maps Embed API" habilitada en el Cloud Project.
 * Ver README para los pasos de configuracion.
 *
 * La key se lee desde import.meta.env.VITE_GOOGLE_MAPS_API_KEY (configurada en .env.local
 * del frontend). Si no esta seteada, mostramos un mensaje de error.
 *
 * Doc: https://developers.google.com/maps/documentation/embed/embedding-map
 */
export function DeviceLocationTab({ device }: Props) {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;
  const loc = device.lastKnownLocation;

  if (!loc) {
    return (
      <Empty
        description={
          <Space direction="vertical" align="center">
            <Text>No hay ubicacion reportada por Google.</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Para que Google reporte la ubicacion, el perfil debe tener configurado{' '}
              <Text code>locationMode=HIGH_ACCURACY</Text> y el device debe haber sincronizado.
            </Text>
          </Space>
        }
      />
    );
  }

  const mapUrl = apiKey
    ? `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${loc.latitude},${loc.longitude}&zoom=16`
    : null;

  const externalGoogleMapsUrl = `https://www.google.com/maps?q=${loc.latitude},${loc.longitude}`;

  const handleCopyCoords = () => {
    navigator.clipboard.writeText(`${loc.latitude},${loc.longitude}`);
  };

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card type="inner" title={<Space><EnvironmentOutlined />Ultima ubicacion conocida</Space>} size="small">
        <Descriptions column={{ xs: 1, sm: 2 }} size="small">
          <Descriptions.Item label="Latitud">
            <Text code>{loc.latitude.toFixed(6)}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Longitud">
            <Text code>{loc.longitude.toFixed(6)}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Reportado">
            {dayjs(loc.reportedAt).format('DD/MM/YYYY HH:mm:ss')}
            <Text type="secondary"> ({dayjs(loc.reportedAt).fromNow()})</Text>
          </Descriptions.Item>
          {loc.accuracyMeters != null && (
            <Descriptions.Item label="Precision">
              ±{Math.round(loc.accuracyMeters)} m
            </Descriptions.Item>
          )}
        </Descriptions>

        <Space style={{ marginTop: 12 }}>
          <Button icon={<CopyOutlined />} onClick={handleCopyCoords} size="small">
            Copiar coordenadas
          </Button>
          <Button icon={<GlobalOutlined />} href={externalGoogleMapsUrl} target="_blank" rel="noopener" size="small">
            Abrir en Google Maps
          </Button>
        </Space>
      </Card>

      {!apiKey ? (
        <Alert
          type="warning"
          showIcon
          message="Google Maps API key no configurada"
          description={
            <Space direction="vertical">
              <Text>
                Para ver el mapa embebido, configurar <Text code>VITE_GOOGLE_MAPS_API_KEY</Text> en
                <Text code> frontend/.env.local</Text> con una key de Google Cloud que tenga habilitada
                la <Text code>Maps Embed API</Text>.
              </Text>
              <Paragraph type="secondary" style={{ fontSize: 12, margin: 0 }}>
                Mientras tanto podes usar "Abrir en Google Maps" para ver la ubicacion.
              </Paragraph>
            </Space>
          }
        />
      ) : (
        <Card type="inner" title="Mapa" size="small" bodyStyle={{ padding: 0 }}>
          <iframe
            title={`Ubicacion device ${device.id}`}
            width="100%"
            height="500"
            style={{ border: 0, display: 'block' }}
            loading="lazy"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
            src={mapUrl!}
          />
        </Card>
      )}
    </Space>
  );
}
